"use client"

import React from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import ImageWithFallback from "@/components/ui/image-with-fallback"
// Minimal profile shape to support both PublicProfile and ProfileSummary inputs
type GridCardProfile = {
  id: string
  name: string
  title?: string
  image?: string | null
  imageUrl?: string | null
  bio?: string
}

export interface ProfileGridCardProps {
  profile: GridCardProfile
  onView: (profile: GridCardProfile) => void
  className?: string
}

function getInitials(name?: string) {
  if (!name) return "EP"
  const parts = name.trim().split(/\s+/)
  const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase() || "").join("")
  return initials || "EP"
}

export default function ProfileGridCard({ profile, onView, className }: ProfileGridCardProps) {
  const handleView = () => {
    onView(profile)
  }

  return (
    <div
      onClick={handleView}
      className={cn(
        "group bg-white border border-gray-200 rounded-2xl shadow-soft hover:shadow-soft-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden profile-card flex flex-col h-full cursor-pointer",
        className
      )}
      role="article"
      aria-label={`${profile.name} – ${profile.title || "Executive"}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleView()
        }
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          handleView()
        }}
        title={`View ${profile.name}`}
        aria-label={`View ${profile.name} profile`}
        className="group/image aspect-square sm:aspect-[5/6] md:aspect-[4/5] w-full bg-gray-100 overflow-hidden relative flex items-center justify-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#5a82bd]"
      >
        <ImageWithFallback
          imageUrl={(profile.image ?? profile.imageUrl ?? null) || undefined}
          name={profile.name}
          alt={`${profile.name} headshot`}
          className="absolute inset-0 w-full h-full transition-transform duration-500 group-hover:scale-105"
          // Disable fixed inline sizing from ImageWithFallback so it can fill
          // the available space within the aspect-ratio container.
          size={0}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
          // Force cover for consistent profile display
          fit="cover"
          objectPositionClass="object-center"
          imgClassName="w-full h-full object-cover"
          rounded="none"
        />
      </button>

      <div className="p-4 sm:p-5 md:p-6 text-center flex flex-col flex-1">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2 uppercase tracking-wide min-h-[2.5rem]">{profile.name}</h3>
        
        <div className="mt-2 sm:mt-3 flex-1 flex items-start">
          <p className="text-sm text-gray-600 line-clamp-2 sm:line-clamp-3 leading-relaxed w-full">
            {profile.bio || profile.title || " "}
          </p>
        </div>

        <div className="mt-3 sm:mt-4">
          <Button
            onClick={(e) => {
              e.stopPropagation()
              handleView()
            }}
            size="sm"
            className={cn(
              "w-full rounded-full tracking-wide text-white text-xs sm:text-sm",
              "bg-[#6b93ce] hover:bg-[#5a82bd] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#5a82bd]",
              "py-2 px-4"
            )}
            aria-label={`View profile for ${profile.name}`}
          >
            VIEW PROFILE
          </Button>
        </div>
      </div>
    </div>
  )
}

 
